import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import PageLayout from '../../components/PageLayout'
import Spinner from '../../components/Spinner'
import { prefetchStripePrices } from '../../helpers/supabase'

const PREPARING_DURATION_MS = 3000

interface Props {
  onPrepare?: () => Promise<{ success: boolean }>
  redirectTo?: string
}

export default function PreparingPage({
  onPrepare,
  redirectTo = '/checkout/plans',
}: Props = {}) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // The dwell runs alongside `onPrepare` rather than after it, so the visitor
  // waits `max(prepare, 3s)` instead of `prepare + 3s`. Starting the timer only
  // once the call resolved stacked a cold edge function invocation on top of the
  // full dwell, all of it immediately before the paywall.
  //
  // Navigation also no longer depends on the call succeeding. Whatever
  // `onPrepare` persists is best-effort; gating on `success` left a visitor
  // stranded here indefinitely, with no error and no way forward, whenever it
  // failed.
  useEffect(() => {
    let cancelled = false

    // The plans loader this page redirects into awaits the price list before it
    // can paint, so warm it during the wait rather than after it.
    prefetchStripePrices()

    const dwell = new Promise((resolve) =>
      setTimeout(resolve, PREPARING_DURATION_MS),
    )
    const prepare = onPrepare
      ? onPrepare().catch(() => undefined)
      : Promise.resolve()

    Promise.all([dwell, prepare]).then(() => {
      if (!cancelled) navigate(redirectTo)
    })

    return () => {
      cancelled = true
    }
  }, [navigate, onPrepare, redirectTo])

  return (
    <PageLayout>
      <div className="flex grow flex-col items-center justify-center">
        <div className="w-full max-w-xs space-y-5 p-8 text-center">
          <h1 className="font-headline text-3xl">
            {t('pages.signUp.loading.heading')}
          </h1>

          <p className="font-sans text-sm text-neutral-500">
            {t('pages.signUp.loading.analyzingResponses')}
          </p>
          <Spinner />
        </div>
      </div>
    </PageLayout>
  )
}
